import { Badge } from "@/components/ui/Badge";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Switch } from "@/components/ui/Switch";
import { TextArea } from "@/components/ui/Textarea";
import { TagsVariationEditor } from "@/components/tool-variation-tags-editor";
import {
  Tool,
  getToolSourceLabel,
  getToolTypeLabel,
  toolSupportsAnnotations,
} from "@/lib/toolTypes";
import { Button } from "@/components/ui/Button";
import { FileCode, PencilRuler, SquareFunction } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { McpIcon } from "@/components/ui/McpIcon";

function getToolIcon(tool: Tool) {
  if (tool.type === "http") return FileCode;
  if (tool.type === "function") return SquareFunction;
  if (tool.type === "platform") return McpIcon;
  return PencilRuler;
}

/** Get the effective annotation value: variation override > base annotation > undefined */
function getAnnotationValue(
  tool: Tool,
  field:
    | "readOnlyHint"
    | "destructiveHint"
    | "idempotentHint"
    | "openWorldHint",
): boolean | undefined {
  if (!toolSupportsAnnotations(tool)) return undefined;
  const variationVal = tool.variation?.[field];
  if (variationVal !== undefined) return variationVal;
  return tool.annotations?.[field];
}

export type ToolUpdatePayload = {
  name: string;
  description: string;
  title?: string;
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
  // tri-state:
  //   undefined = no override (use source tags)
  //   []        = explicit empty override
  //   [...]     = explicit override
  // Key is always present for HTTP tools (so the spread in the upsert form
  // correctly overwrites a prior variation), absent for other tool types.
  tags?: string[] | undefined;
};

function tagsEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

interface EditToolDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool: Tool | null;
  documentIdToName?: Record<string, string>;
  functionIdToName?: Record<string, string>;
  onSave: (updates: ToolUpdatePayload) => void | Promise<void>;
  onRemove?: () => void;
}

export function EditToolDialog({
  open,
  onOpenChange,
  tool,
  documentIdToName,
  functionIdToName,
  onSave,
  onRemove,
}: EditToolDialogProps): JSX.Element | null {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [readOnlyHint, setReadOnlyHint] = useState<boolean | undefined>();
  const [destructiveHint, setDestructiveHint] = useState<boolean | undefined>();
  const [idempotentHint, setIdempotentHint] = useState<boolean | undefined>();
  const [openWorldHint, setOpenWorldHint] = useState<boolean | undefined>();
  // TODO: extend tag variations to prompt tools once they support tags.
  const [tags, setTags] = useState<string[] | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const hasAnnotations = tool ? toolSupportsAnnotations(tool) : false;
  const supportsTags = tool?.type === "http" || tool?.type === "function";
  // Memoize: the inline `[]` fallback would produce a fresh reference per
  // render and invalidate downstream memoization in TagsVariationEditor.
  const baseTags = useMemo(
    () => (tool?.type === "http" || tool?.type === "function" ? tool.tags : []),
    [tool],
  );
  const origTags = supportsTags ? tool?.variation?.tags : undefined;
  const origTitle = tool?.variation?.title ?? tool?.annotations?.title ?? "";
  const origReadOnly = tool
    ? getAnnotationValue(tool, "readOnlyHint")
    : undefined;
  const origDestructive = tool
    ? getAnnotationValue(tool, "destructiveHint")
    : undefined;
  const origIdempotent = tool
    ? getAnnotationValue(tool, "idempotentHint")
    : undefined;
  const origOpenWorld = tool
    ? getAnnotationValue(tool, "openWorldHint")
    : undefined;

  const hasChanges =
    !!tool &&
    (name !== tool.name ||
      title !== origTitle ||
      description !== (tool.description || "") ||
      readOnlyHint !== origReadOnly ||
      destructiveHint !== origDestructive ||
      idempotentHint !== origIdempotent ||
      openWorldHint !== origOpenWorld ||
      (supportsTags && !tagsEqual(tags, origTags)));

  // Reset form when tool changes
  useEffect(() => {
    if (tool) {
      setName(tool.name);
      setTitle(tool.variation?.title ?? tool.annotations?.title ?? "");
      setDescription(tool.description || "");
      setReadOnlyHint(getAnnotationValue(tool, "readOnlyHint"));
      setDestructiveHint(getAnnotationValue(tool, "destructiveHint"));
      setIdempotentHint(getAnnotationValue(tool, "idempotentHint"));
      setOpenWorldHint(getAnnotationValue(tool, "openWorldHint"));
      setTags(
        tool.type === "http" || tool.type === "function"
          ? tool.variation?.tags
          : undefined,
      );
    }
  }, [tool]);

  // Focus name input when dialog opens
  useEffect(() => {
    if (open && nameInputRef.current) {
      // Small delay to ensure dialog animation completes
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+Enter or Ctrl+Enter to save
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void handleSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSave reads current state; listing all state values ensures fresh closure
  }, [
    open,
    name,
    title,
    description,
    readOnlyHint,
    destructiveHint,
    idempotentHint,
    openWorldHint,
    tags,
  ]);

  const handleSave = async () => {
    if (!tool || !hasChanges || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await onSave({
        name,
        description,
        ...(hasAnnotations && {
          title: title || undefined,
          readOnlyHint,
          destructiveHint,
          idempotentHint,
          openWorldHint,
        }),
        // tags key must always be present for HTTP tools so the upsert form
        // spread correctly overwrites any prior variation tags (including
        // when the user resets to source — sending undefined drops the key
        // from the wire body, signalling no override).
        ...(supportsTags && { tags }),
      });
      onOpenChange(false);
    } catch {
      // The mutation owner reports the error. Keep the editor open for retry.
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleRemove = () => {
    if (savingRef.current) return;
    onRemove?.();
    onOpenChange(false);
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && savingRef.current) return;
    onOpenChange(nextOpen);
  };

  const handleClose = () => {
    handleDialogOpenChange(false);
  };

  if (!tool) return null;

  const ToolIcon = getToolIcon(tool);
  const source = getToolSourceLabel(tool, {
    documentIdToName,
    functionIdToName,
  });
  const typeLabel = getToolTypeLabel(tool);

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <Dialog.Content className="max-w-3xl min-w-2xl">
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2">
            <ToolIcon className="text-muted-foreground size-4" />
            <span>{source}</span>
            <Badge variant="neutral" className="text-xs">
              {typeLabel}
            </Badge>
          </Dialog.Title>
        </Dialog.Header>

        {/* Editable fields */}
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="tool-name" className="text-sm font-medium">
              Name
            </Label>
            <Input
              id="tool-name"
              ref={nameInputRef}
              value={name}
              onChange={(value) => setName(value)}
              placeholder="Tool name"
            />
          </div>

          {hasAnnotations && (
            <div className="space-y-2">
              <Label htmlFor="tool-title" className="text-sm font-medium">
                Title
              </Label>
              <Input
                id="tool-title"
                value={title}
                onChange={(value) => setTitle(value)}
                placeholder="Display name override"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="tool-description" className="text-sm font-medium">
              Description
            </Label>
            <TextArea
              id="tool-description"
              value={description}
              onChange={(value) => setDescription(value)}
              placeholder="Add a description for this tool..."
              rows={4}
            />
          </div>

          {supportsTags && (
            <TagsVariationEditor
              id="tool-tags"
              baseTags={baseTags}
              value={tags}
              onChange={setTags}
            />
          )}

          {/* Behavior Hints */}
          {hasAnnotations && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Behavior Hints</Label>
              <p className="text-muted-foreground text-xs">
                Override how this tool is presented to AI models.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Read-only</p>
                    <p className="text-muted-foreground text-xs">
                      Tool does not modify its environment
                    </p>
                  </div>
                  <Switch
                    checked={readOnlyHint ?? false}
                    onCheckedChange={setReadOnlyHint}
                    aria-label="Read-only hint"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Destructive</p>
                    <p className="text-muted-foreground text-xs">
                      Tool may perform destructive updates
                    </p>
                  </div>
                  <Switch
                    checked={destructiveHint ?? false}
                    onCheckedChange={setDestructiveHint}
                    aria-label="Destructive hint"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Idempotent</p>
                    <p className="text-muted-foreground text-xs">
                      Repeated calls with same arguments have no additional
                      effect
                    </p>
                  </div>
                  <Switch
                    checked={idempotentHint ?? false}
                    onCheckedChange={setIdempotentHint}
                    aria-label="Idempotent hint"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Open-world</p>
                    <p className="text-muted-foreground text-xs">
                      Tool interacts with external entities
                    </p>
                  </div>
                  <Switch
                    checked={openWorldHint ?? false}
                    onCheckedChange={setOpenWorldHint}
                    aria-label="Open-world hint"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between border-t pt-4">
          {onRemove && (
            <Button
              variant="destructive-secondary"
              onClick={handleRemove}
              disabled={saving}
            >
              Remove
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={!hasChanges || saving}
            >
              <Button.Text>{saving ? "Saving..." : "Save"}</Button.Text>
              {hasChanges && !saving && (
                <Button.RightIcon>
                  <span className="text-xs opacity-60">⌘⏎</span>
                </Button.RightIcon>
              )}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
